import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import LogisticsRiskSidePanel from "../../features/Logistics/components/details/LogisticsRiskSidePanel";
import AssetProgressTracker from "../../features/Logistics/components/progress/AssetProgressTracker";
import useLogisticsProgressQuery, {
  getLogisticsProgressIdentity,
  isLogisticsInsightsCountableAsset,
} from "../../features/Logistics/hooks/useLogisticsProgressQuery";
import {
  isTrackedLogisticsAsset,
  mergeLogisticsProgressIntoAssets,
} from "../../features/Logistics/models/logisticsProgressPayload";
import {
  getLogisticsMilestoneProgressDisplay,
} from "../../features/Logistics/utils/logisticsMilestoneDetails";
import { getLogisticsProgressData } from "../../utils/logisticsProgress";
import React, { memo, useMemo } from "react";

const ACTIVE_STAGE_GREEN = "#2E7D32";

const LogisticsProgressPanel = ({ item, insightItems = [] }) => {
  const selectedIdentity = getLogisticsProgressIdentity(item);
  const progressTargets = useMemo(() => (item ? [item] : []), [item]);
  const insightTargets = useMemo(
    () =>
      Array.isArray(insightItems)
        ? insightItems.filter(
            (insightItem) =>
              insightItem &&
              isLogisticsInsightsCountableAsset(insightItem) &&
              getLogisticsProgressIdentity(insightItem) !== selectedIdentity,
          )
        : [],
    [insightItems, selectedIdentity],
  );
  const {
    progressResults,
  } = useLogisticsProgressQuery(progressTargets);
  const {
    statistics: progressStatistics,
  } = useLogisticsProgressQuery(insightTargets, {
    includeStatistics: true,
  });
  const displayItem = useMemo(() => {
    if (!item) return item;

    return mergeLogisticsProgressIntoAssets([item], progressResults)[0];
  }, [item, progressResults]);
  const { displayName, stageConfig, stages } =
    getLogisticsProgressData(displayItem);
  const title = displayName
    ? `${displayName} Logistics Progress`
    : "Asset Logistics Progress";
  const progressPercent = Number(displayItem?.logisticsProgressPercent ?? 0);
  const milestoneDisplay = getLogisticsMilestoneProgressDisplay({
    stageKey: displayItem?.logisticsStage,
    milestone: displayItem?.logisticsMilestone,
    detailText: displayItem?.logisticsDetailText,
    detailDate: displayItem?.logisticsDetailDate,
  });
  const showPrimaryMilestone =
    !milestoneDisplay.hidePrimaryWhenDetail || !milestoneDisplay.detailValue;

  // Equipment is the parent container asset — only its tiers/components/
  // instruments are tracked in Logistics, so it never gets a stage
  // tracker/title of its own. The Insights section below is statistics for
  // its tracked children and stays available regardless.
  const showTracker = isTrackedLogisticsAsset(item);

  return (
    <Box
      id="logistics-progress-panel"
      sx={{
        mt: 3,
        mb: 2,
        px: 0,
        py: 2,
        scrollMarginTop: 16,
      }}
    >
      {showTracker && (
        <>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: "primary.main",
              lineHeight: 1.35,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              mt: 0.45,
              mb: 0.9,
              fontSize: 12,
              color: "text.secondary",
            }}
          >
            Current stage:{" "}
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: 0.85,
                py: 0.2,
                borderRadius: 999,
                fontWeight: 700,
                color: ACTIVE_STAGE_GREEN,
                backgroundColor: alpha(ACTIVE_STAGE_GREEN, 0.1),
                border: `1px solid ${alpha(ACTIVE_STAGE_GREEN, 0.22)}`,
              }}
            >
              {stageConfig.label}
            </Box>
          </Typography>
          <Box sx={{ mb: 1.75 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
              }}
            >
              {showPrimaryMilestone ? (
                <Typography
                  sx={{
                    minWidth: 0,
                    fontSize: 12,
                    fontWeight: 700,
                    color: "text.secondary",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {milestoneDisplay.primaryValue}
                </Typography>
              ) : (
                <Box />
              )}
              <Typography
                sx={{
                  flexShrink: 0,
                  fontSize: 12,
                  fontWeight: 800,
                  color: ACTIVE_STAGE_GREEN,
                }}
              >
                {progressPercent}%
              </Typography>
            </Box>

            {milestoneDisplay.detailRows.length > 0 ? (
              <Box
                sx={{
                  mt: 0.75,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.4,
                }}
              >
                {milestoneDisplay.detailRows.map((detail) => (
                  <Box
                    key={detail.label}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        flexShrink: 0,
                        fontSize: 12,
                        fontWeight: 700,
                        color: "text.secondary",
                      }}
                    >
                      {detail.label}
                    </Typography>
                    <Typography
                      title={detail.value}
                      sx={{
                        minWidth: 0,
                        fontSize: 12,
                        fontWeight: 800,
                        color: "text.primary",
                        textAlign: "right",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {detail.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : null}
          </Box>

          <Box
            sx={{
              width: "100%",
              overflowX: "auto",
              pb: 0.5,
              "&::-webkit-scrollbar": { height: 5 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "grey.400",
                borderRadius: 999,
              },
            }}
          >
            <Box sx={{ minWidth: 420 }}>
              <AssetProgressTracker
                stages={stages}
                nodeSize={26}
                nodeColumnWidth={72}
                connectorHeight={5}
              />
            </Box>
          </Box>
        </>
      )}

      <LogisticsRiskSidePanel
        asset={displayItem}
        statistics={progressStatistics}
        variant="embedded"
      />
    </Box>
  );
};

export default memo(LogisticsProgressPanel);
